import {
  lockRoute,
  setRequests,
  setRequestStatus,
  setScheduleRequired,
  updateStop,
  setStops,
} from '../reducers/route';
import {
  setDeferRequest,
  setRejectRequest,
  updateRouteRequestsWithMapper,
  generateTransferResponse,
  generateMergeToStopRequests,
  getValidateParcelList,
  isRequestFromRoutePlanner,
  isAddRequest,
  processAcceptRequest,
  isShopUnsuitableRequest,
  getRequestsParcelCodes,
  getRequestsByParcelCodes,
  getDeferredRequests,
  resetResponse,
  updateRequestsResponseWithStopNumber,
  hasAnyPlannedAddRequest,
} from '../../models/request';
import { DISMISS } from '../../constants/strings';
import { validateStopMerge, processRequestStop } from './stop';
import {
  getCurrentRouteId,
  getRouteRequests,
  getRouteStops,
  getRouteDetail,
  getRequestOrder,
  getCurrentRouteDate,
} from '../selectors/route';
import {
  requestTypes,
  responseTypes,
  requestOriginTypes,
} from '../../enums/requestTypes';
import { showNotification } from './notifications';
import { NotificationType } from '../../features/Notifier';
import {
  getProcessRequestUpdatedStops,
  getStopAndStopIndexByStopNumber,
} from '../../models/stop';
import { arrayToObject, arrayToObjectByValue } from '../../utils/array';
import { getRoutesTransfer } from '../../services/routes';
import { prepareTransferDriversRoutes } from '../../features/request/model';
import { getStopByNumber } from '../../features/routeDetail/model';
import { getErrorMessage } from '../../utils/errors';
import { parcelsRemoval, parcelsValidation } from '../../models/parcel';
import { getPreparedStops } from '../selectors/ui/routeDetail';
import {
  processRequestStopInList,
  updateStopInStopList,
} from '../../models/route';
import { allowConsignmentSplitting } from '../selectors/app';
import { clearSelection } from '../reducers/ui/routeDetail/stopFilters';

const updateRouteRequests = (updatedRequests) => (dispatch) => {
  dispatch(setRequests(updatedRequests));
  dispatch(setScheduleRequired(true));
};

const createRequestAction =
  (mapper) => (requests) => async (dispatch, getState) => {
    const currentUserLock = await dispatch(lockRoute());

    if (!currentUserLock) {
      return false;
    }

    const routeRequests = getRouteRequests(getState());
    const updateRequestsMap = arrayToObject(requests, 'requestId');

    routeRequests.forEach((request) => {
      const { requestId } = request;
      const requestInMap = updateRequestsMap[requestId];

      if (!requestInMap) {
        updateRequestsMap[requestId] = request;
      } else if (mapper) {
        updateRequestsMap[requestId] = mapper(requestInMap);
      }
    });
    dispatch(updateRouteRequests(Object.values(updateRequestsMap)));
    return true;
  };

export const onDeferRequest = (requests) => (dispatch, getState) => {
  const routeDetail = getRouteDetail(getState());
  const stop = getStopByNumber(routeDetail.stop, requests[0].stopNumber);
  return dispatch(createRequestAction(setDeferRequest(stop))(requests));
};

export const resetAllRequestsExceptDeferred = () => (dispatch, getState) => {
  const state = getState();
  const routeRequests = getRouteRequests(state);
  const deferredRequests = getDeferredRequests(routeRequests);
  const updatedRequests = deferredRequests.map((request) =>
    resetResponse(request)
  );

  dispatch(setRequests(updatedRequests));
};

export const onRejectRequest =
  (requests, batchAction, stopsToProcess, requestsToProcess) =>
  async (dispatch, getState) => {
    if (!batchAction) {
      const currentUserLock = await dispatch(lockRoute());
      if (!currentUserLock) {
        return false;
      }
    }
    const state = getState();
    let routeRequests = requestsToProcess || getRouteRequests(state);
    const requestsFromRoutePlanner = requests.filter(isRequestFromRoutePlanner);
    const stops = stopsToProcess || getRouteStops(state);
    const stop = getStopByNumber(stops, requests[0].stopNumber);
    let updatedStops;

    if (requestsFromRoutePlanner.length) {
      const sameRequestFromSaturn = getRequestsByParcelCodes(
        routeRequests,
        getRequestsParcelCodes(requests),
        requestOriginTypes.SATURN
      );
      if (sameRequestFromSaturn.length) {
        routeRequests = await dispatch(onRejectRequest(sameRequestFromSaturn));
      }
      const toValidateParcelList =
        (stop && getValidateParcelList(stop, requestsFromRoutePlanner)) || [];

      if (toValidateParcelList.length > 0) {
        try {
          const updatedStop = await dispatch(
            validateStopMerge({
              parcels: toValidateParcelList,
              checkOpenRequests: false,
            })
          );

          updatedStops = updateStopInStopList(stops, {
            ...stop,
            ...updatedStop,
          });
        } catch (error) {
          dispatch(
            showNotification({
              type: NotificationType.ACTION,
              message: getErrorMessage(error),
              buttonText: DISMISS,
            })
          );
          return null;
        }
      } else {
        dispatch(clearSelection());
        if (stop) {
          updatedStops = processRequestStopInList(stops, stop, requests);
        }
      }
    }
    const updatedRequests = updateRouteRequestsWithMapper(
      routeRequests,
      requests,
      setRejectRequest,
      stop
    );

    if (batchAction) {
      return { updatedStops, updatedRequests };
    }
    updatedStops && dispatch(setStops(updatedStops));
    dispatch(updateRouteRequests(updatedRequests));
    return updatedRequests;
  };

export const getAvailableRoutesTransfer =
  (depot, date, uid, uniqueNextWaveRoutes = []) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const routes = await getRoutesTransfer(depot, date, uid);
      const currentRouteId = getCurrentRouteId(state);

      return prepareTransferDriversRoutes(
        routes,
        currentRouteId,
        uniqueNextWaveRoutes
      );
    } catch {
      return [];
    }
  };

export const onConfirmTransferRequest =
  (row, selectedRouteId) => async (dispatch, getState) => {
    const currentUserLock = await dispatch(lockRoute());
    const { requests } = row;
    if (!currentUserLock) {
      return;
    }
    const state = getState();
    const routeDetail = getRouteDetail(state);
    const stop = getStopByNumber(routeDetail.stop, requests[0].stopNumber);
    const toValidateParcelList =
      (stop && getValidateParcelList(stop, requests)) || [];

    if (
      requests[0].requestType !== requestTypes.REMOVE_COLLECTION &&
      toValidateParcelList.length > 0
    ) {
      try {
        const updatedStop = await dispatch(
          validateStopMerge({
            parcels: toValidateParcelList,
            checkOpenRequests: false,
          })
        );
        dispatch(updateStop({ ...stop, ...updatedStop }));
      } catch (error) {
        dispatch(
          showNotification({
            type: NotificationType.ACTION,
            message: getErrorMessage(error),
            buttonText: DISMISS,
          })
        );
        return null;
      }
    } else {
      dispatch(clearSelection());
      stop && dispatch(processRequestStop(stop, requests));
    }

    const routeId = getCurrentRouteId(state);
    const routeRequests = getRouteRequests(state);
    const requestOrder = getRequestOrder(state);
    const addRequests = isAddRequest(requests[0]);
    const updatedRequests = updateRouteRequestsWithMapper(
      routeRequests,
      requests,
      generateTransferResponse(
        routeId,
        selectedRouteId,
        requests[0].stopNumber,
        stop?.stopId,
        addRequests ? responseTypes.REJECT : responseTypes.ACCEPT
      )
    );

    if (isRequestFromRoutePlanner(requests[0])) {
      const newRequests = generateMergeToStopRequests(
        requests,
        selectedRouteId,
        requestOrder
      );
      dispatch(updateRouteRequests(updatedRequests.concat(newRequests)));
    } else {
      dispatch(updateRouteRequests(updatedRequests));
    }

    return updatedRequests;
  };

export const onAcceptRequest =
  ({ requests, ...stop }, batchAction = false, stopsToProcess) =>
  async (dispatch, getState) => {
    if (!batchAction) {
      const currentUserLock = await dispatch(lockRoute());
      if (!currentUserLock) {
        return;
      }
      dispatch(setRequestStatus(true));
    }
    const state = getState();
    const routeId = getCurrentRouteId(state);
    const routeDate = getCurrentRouteDate(state);
    const stops = stopsToProcess || getRouteStops(state);
    const routeRequests = getRouteRequests(state);

    const processedRequest = requests[0];
    const processStopIndex = getStopAndStopIndexByStopNumber(
      stops,
      processedRequest.stopNumber
    );

    const isPlannedAddRequest = hasAnyPlannedAddRequest(requests);

    const {
      newStop,
      removedParcels = [],
      updatedRequests = [],
      validateParcels = [],
      removeStopNumber,
    } = processAcceptRequest({
      stop,
      stops,
      routeId,
      requests,
      routeRequests,
      processedRequest,
      processStopIndex,
      processStop: stops[processStopIndex],
      routeDate,
    });

    const updatedStops = getProcessRequestUpdatedStops({
      stops,
      newStop,
      removeStopNumber,
    });
    const { finalStops = [], requestsToSave } = await dispatch(
      acceptRequestGathering({
        routeRequests,
        stop,
        updatedStops,
        removedParcels,
        updatedRequests,
        validateParcels,
        processedRequest,
        processStopIndex,
        isPlannedAddRequest,
        batchAction,
      })
    );
    if (batchAction) {
      return { updatedStops: finalStops, updatedRequests: requestsToSave };
    }
    dispatch(setRequestStatus(false));

    if (finalStops.length) {
      dispatch(setStops(finalStops));
    }
    return finalStops.length;
  };

export const acceptRequestGathering =
  ({
    routeRequests,
    stop,
    updatedStops,
    removedParcels,
    validateParcels,
    updatedRequests,
    processedRequest,
    processStopIndex,
    isPlannedAddRequest,
    batchAction,
  }) =>
  async (dispatch, getState) => {
    try {
      const allowDepotConsignmentSplitting =
        allowConsignmentSplitting(getState());
      const parcelValidated =
        !isPlannedAddRequest || allowDepotConsignmentSplitting;
      const finalStops = [...updatedStops];
      let requestsToSave = [...updatedRequests];
      if (validateParcels.length) {
        const stopFromApi = await dispatch(
          validateStopMerge({
            parcels: validateParcels,
            checkOpenRequests: false,
            parcelValidated,
          })
        );
        const { newStop, updatedStop } = parcelsValidation({
          stop,
          stopFromApi,
          updatedStops,
          processedRequest,
          processStopIndex,
        });

        if (newStop) {
          if (processedRequest.addAfterStop) {
            const previousStop = finalStops.find(
              (s) => s.stopNumber === processedRequest.addAfterStop
            );
            if (previousStop) {
              newStop.sequenceNumber = previousStop.sequenceNumber + 1;
              for (let i = 0; i < finalStops.length; i++) {
                if (finalStops[i].sequenceNumber >= newStop.sequenceNumber) {
                  finalStops[i] = {
                    ...finalStops[i],
                    sequenceNumber: finalStops[i].sequenceNumber + 1,
                  };
                }
              }

              finalStops.push(newStop);
            } else {
              finalStops.push(newStop);
            }
          } else {
            finalStops.push(newStop);
          }
        }
        if (updatedStop) {
          finalStops[processStopIndex] = updatedStop;
        }
      }

      if (removedParcels.length) {
        const shopUnsuitable = isShopUnsuitableRequest(processedRequest);
        const stopFromApi = await dispatch(
          validateStopMerge({
            parcels: removedParcels,
            checkOpenRequests: false,
            shopUnsuitable,
          })
        );

        const { newStop } = parcelsRemoval({
          stopFromApi,
          updatedStops,
          routeRequests,
        });
        if (newStop) {
          if (shopUnsuitable) {
            requestsToSave = updateRequestsResponseWithStopNumber(
              updatedRequests,
              newStop
            );
          }
          finalStops.push(newStop);
        }
      }

      if (batchAction) {
        return { finalStops, requestsToSave };
      }

      await dispatch(createRequestAction()(requestsToSave));

      return { finalStops };
    } catch (error) {
      dispatch(setRequestStatus(false));
      dispatch(
        showNotification({
          type: NotificationType.ACTION,
          message: getErrorMessage(error),
          buttonText: DISMISS,
        })
      );

      return {};
    }
  };

export const onAcceptRequestBatchAction =
  (requests) => async (dispatch, getState) => {
    const currentUserLock = await dispatch(lockRoute());
    if (!currentUserLock) {
      return;
    }
    dispatch(setRequestStatus(true));
    const state = getState();

    const stops = getRouteStops(state);
    const stopsFromList = getPreparedStops(state);
    const stopsToProcess = Object.values(
      arrayToObjectByValue(requests, 'stopNumber')
    ).map((requestObj) => {
      const stop = stopsFromList.find(
        (el) =>
          el.requests &&
          el.requests.some(
            ({ parcelCode }) => parcelCode === requestObj[0].parcelCode
          )
      );
      return stop
        ? { ...stop, requests: requestObj }
        : { requests: requestObj, stopNumber: requestObj[0].stopNumber };
    });

    const dataToUpdate = await stopsToProcess.reduce(
      async (updatedData, stop) => {
        try {
          let data = await updatedData;
          const { updatedStops, updatedRequests } = await dispatch(
            onAcceptRequest(stop, true, data.updatedStops)
          );
          return {
            updatedStops,
            updatedRequests: [...data.updatedRequests, ...updatedRequests],
          };
        } catch (error) {
          dispatch(setRequestStatus(false));
          dispatch(
            showNotification({
              type: NotificationType.ACTION,
              message: getErrorMessage(error),
              buttonText: DISMISS,
            })
          );

          return {};
        }
      },
      Promise.resolve({ updatedStops: stops, updatedRequests: [] })
    );

    if (dataToUpdate.updatedRequests.length) {
      await dispatch(createRequestAction()(dataToUpdate.updatedRequests));
      await dispatch(setStops(dataToUpdate.updatedStops));
    }
    dispatch(setRequestStatus(false));
    return dataToUpdate.updatedRequests.length;
  };

export const onRejectRequestBatchAction =
  (requests) => async (dispatch, getState) => {
    const currentUserLock = await dispatch(lockRoute());
    if (!currentUserLock) {
      return;
    }
    const state = getState();
    const stops = getRouteStops(state);
    const routeRequests = getRouteRequests(state);
    const groupedRequest = arrayToObjectByValue(requests, 'stopNumber');

    const dataToUpdate = await Object.values(groupedRequest).reduce(
      async (updatedData, proceedRequests) => {
        try {
          let data = await updatedData;
          const { updatedStops, updatedRequests } = await dispatch(
            onRejectRequest(
              proceedRequests,
              true,
              data.updatedStops,
              data.updatedRequests
            )
          );
          return {
            updatedStops,
            updatedRequests,
          };
        } catch (error) {
          dispatch(
            showNotification({
              type: NotificationType.ACTION,
              message: getErrorMessage(error),
              buttonText: DISMISS,
            })
          );

          return null;
        }
      },
      Promise.resolve({ updatedStops: stops, updatedRequests: routeRequests })
    );

    if (dataToUpdate) {
      await dispatch(setStops(dataToUpdate.updatedStops));
      await dispatch(updateRouteRequests(dataToUpdate.updatedRequests));
    }
    return dataToUpdate;
  };
